"use client";

import { useEffect, useRef, useState } from "react";
import { API_BASE_URL } from "@/lib/api";

export type ScreenshotSignal = {
  type: "ios_audioctx" | "visibility_focus" | "keyboard" | "media_recording";
  userAgent: string;
  timestamp: string;
};

/* Module-level state to avoid spamming 404s when the endpoint is unavailable */
let endpointDisabled = false;
let lastLogTime = 0;
const LOG_COOLDOWN_MS = 2000;

async function logScreenshotEvent(signal: ScreenshotSignal) {
  if (endpointDisabled) return;
  const now = Date.now();
  if (now - lastLogTime < LOG_COOLDOWN_MS) return;
  lastLogTime = now;

  try {
    const url = `${API_BASE_URL}/api/v1/analytics/screenshot`;
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Supabase-Token":
          typeof window !== "undefined"
            ? (window as any).__SUPABASE_TOKEN || ""
            : "",
      },
      body: JSON.stringify({
        user_id: (window as any).__USER_ID || null,
        ...signal,
      }),
    });
    // If the endpoint doesn't exist, stop trying to avoid console spam
    if (response.status === 404) {
      endpointDisabled = true;
    }
  } catch {
    // best-effort logging
  }
}

export function useScreenshotGuard(options?: { userName?: string | null }) {
  const [showOverlay, setShowOverlay] = useState(false);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const audioBufferRef = useRef<AudioBuffer | null>(null);
  const initializedRef = useRef(false);
  const showingRef = useRef(false);
  const mayHaveCapturedRef = useRef(false);
  const timeoutRef = useRef<number | null>(null);

  useEffect(() => {
    let cancelled = false;

    const showOverlayThenHide = (type: ScreenshotSignal["type"]) => {
      if (showingRef.current) return;
      showingRef.current = true;
      setShowOverlay(true);
      const signal: ScreenshotSignal = {
        type,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
      };
      logScreenshotEvent(signal);
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = window.setTimeout(() => {
        setShowOverlay(false);
        showingRef.current = false;
        timeoutRef.current = null;
      }, 4000);
    };

    /* Hoisted to the effect scope so the unconditional cleanup can remove the
       listeners on desktop too. Previously `handleFirstTouch` was declared
       inside the `if (isIOS)` block_but_referenced_in_the_cleanup, which threw
       ReferenceError on every non-iOS unmount and crashed any page using the
       hook. */
    let handleFirstTouch: (() => void) | undefined;
    /* ---------- iOS AudioContext detection ---------- */
    const isIOS =
      /iPad|iPhone|iPod/.test(navigator.platform) ||
      (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

    if (isIOS) {
      const initAudioContext = () => {
        if (initializedRef.current || !audioCtxRef.current) return;
        initializedRef.current = true;

        const ctx = audioCtxRef.current;
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        gain.gain.value = 0;
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start();

        ctx.onstatechange = () => {
          if (
            ctx.state === "suspended" &&
            document.visibilityState === "visible"
          ) {
            showOverlayThenHide("ios_audioctx");
            if (audioBufferRef.current) {
              const source = ctx.createBufferSource();
              source.buffer = audioBufferRef.current;
              source.connect(ctx.destination);
              source.start();
            }
          }
        };
      };

      handleFirstTouch = () => {
        initAudioContext();
        document.removeEventListener("touchstart", handleFirstTouch!);
        document.removeEventListener("click", handleFirstTouch!);
      };

      audioCtxRef.current = new (
        window.AudioContext || (window as any).webkitAudioContext
      )();
      document.addEventListener("touchstart", handleFirstTouch, {
        once: false,
      });
      document.addEventListener("click", handleFirstTouch, { once: false });

      fetch("/audio/screenshot_warning.mp3")
        .then((res) => (res.ok ? res.arrayBuffer() : null))
        .then((buffer) => {
          if (cancelled || !buffer || !audioCtxRef.current) return;
          return audioCtxRef.current!.decodeAudioData(buffer.slice(0));
        })
        .then((decoded) => {
          if (!cancelled && decoded) {
            audioBufferRef.current = decoded;
          }
        })
        .catch(() => {});
    }

    /* ---------- Screen recording + visibility detection (iOS only) ---------- */
    /* On desktop, the visibility_change proxy fires every time the user switches
       tabs — which during login flows happens constantly — and the 4-second
       overlay (bg-black/30 + backdrop-blur-xl) visually blocks the entire page,
       making it look like content disappeared. Restrict these signals to iOS
       where the screenshot-detection logic is actually needed. */
    if (isIOS) {
      let mediaRecorderHandler: (() => void) | null = null;
      if (typeof navigator !== "undefined" && "addEventListener" in navigator) {
        mediaRecorderHandler = () => {
          if (!cancelled && document.visibilityState === "visible") {
            showOverlayThenHide("media_recording");
          }
        };
        (navigator as any).addEventListener(
          "devicechange",
          mediaRecorderHandler,
        );
      }

      /* Visibility / focus-loss proxy — iOS only. There is no browser event for
         an OS-level screenshot. The best-available proxy is the tab losing focus:
         when_the_user_returns, we show the shield briefly (post-hoc deterrence
         + watermark audit). We only show it AFTER a hidden→visible transition,
         never while the app is in normal use, so it does not flicker during
         ordinary in-app focus changes. */
      const handleVisibility = () => {
        if (document.visibilityState === "hidden") {
          mayHaveCapturedRef.current = true;
        } else if (mayHaveCapturedRef.current) {
          mayHaveCapturedRef.current = false;
          showOverlayThenHide("visibility_focus");
        }
      };

      document.addEventListener("visibilitychange", handleVisibility);

      return () => {
        cancelled = true;
        if (timeoutRef.current) {
          window.clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        document.removeEventListener("visibilitychange", handleVisibility);
        if (mediaRecorderHandler && "removeEventListener" in navigator) {
          (navigator as any).removeEventListener(
            "devicechange",
            mediaRecorderHandler,
          );
        }
        audioCtxRef.current?.close().catch(() => {});
        audioCtxRef.current = null;
        initializedRef.current = false;
      };
    }

    /* PrintScreen (Windows) — the closest we get to a screenshot intent on
       desktop. We do NOT block Ctrl/Cmd+P — that's a legitimate print action,
       not a screenshot, and blocking it was part of the "blurs unnecessarily"
       feel. */
    let desktopTimeout: ReturnType<typeof setTimeout> | null = null;
    const handleKeyDown = (event: KeyboardEvent) => {
      const key = typeof event.key === "string" ? event.key.toLowerCase() : "";
      if (key === "printscreen") {
        event.preventDefault();
        event.stopPropagation();
        showOverlayThenHide("keyboard");
      }
    };

    document.addEventListener("keydown", handleKeyDown, true);

    return () => {
      cancelled = true;
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (desktopTimeout) {
        clearTimeout(desktopTimeout);
        desktopTimeout = null;
      }
      document.removeEventListener("keydown", handleKeyDown, true);
      if (isIOS) {
        if (handleFirstTouch) {
          document.removeEventListener("touchstart", handleFirstTouch);
          document.removeEventListener("click", handleFirstTouch);
        }
      }
      audioCtxRef.current?.close().catch(() => {});
      audioCtxRef.current = null;
      initializedRef.current = false;
    };
  }, []);

  return { showOverlay };
}
